import { formatNumericBody } from '@nsftx/seven-client-retail-utils'
import type { FilterImpl } from '@nsftx/seven-client-retail-print-template-engine'
import type { CurrencyDisplay, PrintNumberFormat } from '../interfaces/PrintNumberFormat'
import type { PrintFormatOptions } from '../interfaces/PrintFormatOptions'

export type PrintNumberFormatInstanceConfig = {
  numberFormat: PrintNumberFormat
  currency: string
  currencyDisplay?: CurrencyDisplay
}

export type NumberFormatStyle = 'decimal' | 'currency' | 'percent'

export type NxgFormatNumberFilterArgs = {
  style?: NumberFormatStyle
  currency?: string
  currencyDisplay?: CurrencyDisplay
}

const DEFAULT_CURRENCY_DISPLAY: CurrencyDisplay = 'symbol'

export const getCurrencySymbol = (currencyCode: string): string => {
  if (!currencyCode) {
    return ''
  }

  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).formatToParts(0)

    const symbolPart = parts.find((part) => part.type === 'currency')
    return symbolPart ? symbolPart.value : ''
  } catch {
    return ''
  }
}

export const resolveCurrencyIso = (
  filterCurrency?: string,
  jobFormat?: PrintFormatOptions | null,
  instanceCurrency?: string
): string => {
  if (filterCurrency) {
    return filterCurrency
  }

  if (jobFormat?.currency) {
    return jobFormat.currency
  }

  return instanceCurrency ?? ''
}

export const resolveCurrencyDisplay = (
  filterDisplay?: string,
  instanceDefault?: CurrencyDisplay
): CurrencyDisplay => {
  if (filterDisplay === 'code' || filterDisplay === 'symbol') {
    return filterDisplay
  }

  return instanceDefault ?? DEFAULT_CURRENCY_DISPLAY
}

const parseStyle = (arg: unknown): NumberFormatStyle => {
  if (arg === 'currency' || arg === 'percent') {
    return arg
  }

  return 'decimal'
}

const isNamedPair = (arg: unknown): arg is [string, unknown] =>
  Array.isArray(arg) && arg.length === 2 && typeof arg[0] === 'string'

const parseDisplay = (value: unknown): CurrencyDisplay | undefined =>
  value === 'code' || value === 'symbol' ? value : undefined

export const parseNxgFormatNumberFilterArgs = (
  ...args: unknown[]
): NxgFormatNumberFilterArgs => {
  if (args.length === 0) {
    return { style: 'decimal' }
  }

  if (args.every(isNamedPair)) {
    const map = new Map(args.map(([key, value]) => [key, value]))
    const style = parseStyle(map.get('style'))
    const result: NxgFormatNumberFilterArgs = { style }

    if (style === 'currency') {
      const currency = map.get('currency')
      if (typeof currency === 'string' && currency) {
        result.currency = currency
      }
      result.currencyDisplay = parseDisplay(map.get('display'))
    }

    return result
  }

  if (args.length === 1 && typeof args[0] === 'string') {
    return { style: parseStyle(args[0]) }
  }

  return { style: 'decimal' }
}

export const formatPrintNumber = (
  value: unknown,
  instanceConfig: PrintNumberFormatInstanceConfig,
  jobFormat: PrintFormatOptions | null | undefined,
  filterArgs?: NxgFormatNumberFilterArgs
): string => {
  const style = filterArgs?.style ?? 'decimal'
  const body = formatNumericBody(value, instanceConfig.numberFormat)

  if (style === 'percent') {
    return `${body}%`
  }

  if (style === 'currency') {
    const iso = resolveCurrencyIso(
      filterArgs?.currency,
      jobFormat,
      instanceConfig.currency
    )
    const display = resolveCurrencyDisplay(
      filterArgs?.currencyDisplay,
      instanceConfig.currencyDisplay
    )
    const symbol = getCurrencySymbol(iso)

    if (!symbol) {
      throw new Error(`nxg_format_number: unsupported currency "${iso}"`)
    }

    const fragment = display === 'code' ? iso : symbol
    return `${body} ${fragment}`
  }

  return body
}

export const createNxgFormatNumberFilter = (instanceConfig: PrintNumberFormatInstanceConfig) => {
  function nxgFormatNumberFilter(this: FilterImpl, value: unknown, ...args: unknown[]): string {
    const rawFormat = this.context.getSync(['format'])
    const jobFormat =
      rawFormat && typeof rawFormat === 'object' && !Array.isArray(rawFormat)
        ? (rawFormat as PrintFormatOptions)
        : undefined

    return formatPrintNumber(value, instanceConfig, jobFormat, parseNxgFormatNumberFilterArgs(...args))
  }

  return nxgFormatNumberFilter
}
